/**
 * Çerez tercihi — okuma/yazma ve "bu kategori açık mı" sorgusu.
 *
 * Bugün uygulamada analiz veya reklam çerezi yok (bkz. backend
 * `legal.inventory.ts`), yani `isAllowed('analiz')` bugün kimseyi
 * etkilemiyor. Yine de mekanizma baştan kuruluyor: ileride bir ölçümleme
 * aracı eklendiğinde onu bu kapının ARKASINA koymak, kapıyı sonradan
 * açmaktan çok daha kolay. Kapı yoksa araç doğrudan bağlanır ve rıza
 * tamamen atlanır — pratikte bu hataya en sık böyle düşülüyor.
 *
 * ⚠️ Yeni bir izleme/ölçümleme aracı eklerken: önce `isAllowed(...)`,
 * sonra araç. Ve `legal.inventory.ts`'teki tabloya satırı eklemeyi unutmayın.
 */
import { ConsentCategoryId } from '../services/api';

const STORAGE_KEY = 'dijipo_cookie_consent';
/** 12 ay — Kurul'un çerez rehberinde önerdiği azami tercih ömrü. */
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export interface StoredConsent {
  /** Kabul edilen kategoriler. 'zorunlu' her zaman içindedir. */
  categories: ConsentCategoryId[];
  /** Tercihin verildiği andaki politika sürümü. */
  version: string;
  /** ISO zaman damgası. */
  at: string;
  /** Sunucudaki denetim günlüğüne yazıldı mı (giriş yapıldığında yazılır). */
  reported?: boolean;
}

export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (!parsed || !Array.isArray(parsed.categories) || !parsed.at) return null;
    // Süresi dolmuş tercih = tercih yok; bant yeniden gösterilir.
    if (Date.now() - new Date(parsed.at).getTime() > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    // Gizli sekmede / depolama kapalıyken localStorage erişimi throw edebilir.
    return null;
  }
}

export function writeConsent(categories: ConsentCategoryId[], version: string): StoredConsent {
  const stored: StoredConsent = {
    categories: Array.from(new Set<ConsentCategoryId>(['zorunlu', ...categories])),
    version,
    at: new Date().toISOString(),
    reported: false,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* depolama yoksa tercih oturumluk kalır — bant her açılışta sorar */
  }
  // Reddedilen kategori için daha önce yazılmış kayıtlar burada silinir —
  // aksi hâlde tercih yalnızca ileriye dönük çalışır ve kullanıcı "kapattım"
  // dediği hâlde eski kaydı okunmaya devam ederdi.
  if (!stored.categories.includes('islev')) purgeFunctionalStorage();
  window.dispatchEvent(new CustomEvent('dijipo:cookie-consent', { detail: stored }));
  return stored;
}

export function markConsentReported(): void {
  const current = readConsent();
  if (!current) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, reported: true }));
  } catch {
    /* yoksay */
  }
}

/**
 * Saklanan tercih hâlâ geçerli mi. Politika sürümü yükseldiyse eski tercih
 * yeni kategorileri kapsamıyor demektir; kullanıcıya yeniden sorulur.
 */
export function isConsentCurrent(stored: StoredConsent | null, currentVersion: string): boolean {
  return !!stored && stored.version === currentVersion;
}

/** Zorunlu olmayan bir kategori için rıza var mı. */
export function isAllowed(category: ConsentCategoryId): boolean {
  if (category === 'zorunlu') return true;
  const stored = readConsent();
  return !!stored && stored.categories.includes(category);
}

/**
 * "İşlev" kategorisine giren yerel kayıtların anahtar önekleri.
 *
 * Bu liste `legal.inventory.ts`'teki tablonun "İşlev" satırlarının kod
 * tarafındaki karşılığıdır — biri değişince diğeri de değişmeli, yoksa ya
 * beyan edilmemiş bir kayıt yazılır ya da rıza geri alındığında temizlenmeyen
 * bir kalıntı kalır.
 *
 * ⚠️ Zorunlu anahtarlar (dijipo_token, dijipo_user, dijipo_sso,
 * dijipo_cookie_consent, ve sessionStorage'daki dijipo_next — girişten sonra
 * dönülecek sayfa, utils/returnTo.ts) BURAYA GİRMEZ: onlar rızaya bağlı değil
 * ve silinmeleri oturumu düşürür.
 */
const FUNCTIONAL_KEY_PREFIXES = [
  'dijipo_selected_child',
  'homework-test:',
  'viewed_story_ids',
  'videosPreviewed:',
  'dijipo_week_win_',
];

function isFunctionalKey(key: string): boolean {
  return FUNCTIONAL_KEY_PREFIXES.some((p) => key === p || key.startsWith(p));
}

/**
 * İşlev kaydı yaz — rıza yoksa yazma.
 *
 * Tercih ekranı "Kapatırsanız ... bu tercihler her seferinde sıfırlanır"
 * diyor; o cümleyi doğru kılan şey bu fonksiyon. Doğrudan
 * `localStorage.setItem` çağıran her yeni işlev kaydı, verilen tercihi sessizce
 * geçersiz kılar — bu yüzden işlev kayıtları HER ZAMAN buradan geçmeli.
 */
export function setFunctional(key: string, value: string): void {
  if (!isAllowed('islev')) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* kota dolu / gizli sekme — kayıt sadece o oturumda tutulmaz */
  }
}

/** İşlev kaydını oku. Rıza geri alınmışsa zaten temizlenmiş olur. */
export function getFunctional(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * İşlev rızası geri alındığında yazılmış kayıtları sil.
 *
 * Yalnızca yazmayı durdurmak yetmez: kullanıcı önce kabul edip sonra
 * reddettiğinde eskiden yazılmış kayıtlar okunmaya devam ederdi, yani tercih
 * geçmişe dönük olarak işlemezdi.
 */
export function purgeFunctionalStorage(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && isFunctionalKey(key)) doomed.push(key);
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* depolama okunamıyorsa silinecek bir şey de yok */
  }
}

/** "Çerez Tercihleri" bağlantısı — bandı/tercih ekranını yeniden açar. */
export function openCookiePreferences(): void {
  window.dispatchEvent(new CustomEvent('dijipo:open-cookie-preferences'));
}
